import { useCallback, useEffect, useMemo, useState } from "react";
import type { Question } from "@services/evaluationService";

export const REPORT_SUMMARY_INDEX = -1;

export function useL1ReportSummaryNavigation(
  questions: Question[],
  l1EvaluationReportEnabled: boolean,
  taskKey?: string
) {
  const isReportSummaryEnabled = useMemo(
    () => l1EvaluationReportEnabled && questions.length > 1,
    [l1EvaluationReportEnabled, questions.length]
  );

  const [navigationIndex, setNavigationIndex] = useState(0);

  useEffect(() => {
    setNavigationIndex(0);
  }, [taskKey]);

  const isReportView =
    isReportSummaryEnabled && navigationIndex === REPORT_SUMMARY_INDEX;

  const questionIndex = isReportView ? REPORT_SUMMARY_INDEX : navigationIndex;

  const currentQuestion =
    !isReportView && navigationIndex >= 0
      ? questions[navigationIndex]
      : undefined;

  const handleQuestionSelect = useCallback(
    (index: number) => {
      if (index === REPORT_SUMMARY_INDEX) {
        if (!isReportSummaryEnabled) return;
        setNavigationIndex(REPORT_SUMMARY_INDEX);
        return;
      }
      if (index < 0 || index >= questions.length) return;
      setNavigationIndex(index);
    },
    [isReportSummaryEnabled, questions.length]
  );

  const goToReportSummary = useCallback(() => {
    if (isReportSummaryEnabled) {
      setNavigationIndex(REPORT_SUMMARY_INDEX);
    }
  }, [isReportSummaryEnabled]);

  const goToFirstQuestion = useCallback(() => {
    setNavigationIndex(0);
  }, []);

  const goToPrevQuestion = useCallback(() => {
    if (navigationIndex === 0 && isReportSummaryEnabled) {
      setNavigationIndex(REPORT_SUMMARY_INDEX);
      return;
    }
    if (navigationIndex > 0) {
      setNavigationIndex(navigationIndex - 1);
    }
  }, [navigationIndex, isReportSummaryEnabled]);

  const goToNextQuestion = useCallback(() => {
    if (navigationIndex < questions.length - 1) {
      setNavigationIndex(navigationIndex + 1);
    }
  }, [navigationIndex, questions.length]);

  const goToNextFromReport = useCallback(() => {
    setNavigationIndex(0);
  }, []);

  return {
    isReportSummaryEnabled,
    isReportView,
    questionIndex,
    currentQuestion,
    handleQuestionSelect,
    goToReportSummary,
    goToFirstQuestion,
    goToPrevQuestion,
    goToNextQuestion,
    goToNextFromReport,
  };
}
